import { Card, CardContent } from "@/components/ui/card";
import { MonthData } from "@/types/dashboard";
import { Mask } from "@/utils/mask";
import { Calendar, ShoppingBag, Target, CheckCircle2 } from "lucide-react";

interface MonthCardProps {
  data: MonthData;
  goalType?: "meta" | "super_meta";
}

export function MonthCard({ data, goalType = "meta" }: MonthCardProps) {
  const progress = isNaN(data.progress_percentage) ? 0 : data.progress_percentage;
  const progressClamped = Math.min(progress, 100);
  const isGoalReached = progress >= 100;
  const goalLabel = goalType === "super_meta" ? "Super Meta" : "Meta";

  return (
    <Card className="h-full border-0 shadow-lg bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-white/20 backdrop-blur rounded-lg">
              <Calendar className="h-4 w-4" />
            </div>
            <span className="font-medium">{goalLabel} do Mês</span>
          </div>
          {isGoalReached && <CheckCircle2 className="h-5 w-5 text-emerald-200" />}
        </div>

        {/* Main Value */}
        <div className="mb-5">
          <p className="text-emerald-200 text-xs mb-1">Vendido</p>
          <p className="text-xl font-bold truncate">{Mask.formatCurrency(data.sales_total)}</p>
        </div>

        {/* Goal Progress */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-3 mb-4">
          <div className="flex items-center gap-1 mb-2">
            <Target className="h-3 w-3 text-emerald-200 flex-shrink-0" />
            <span className="text-xs text-emerald-100 truncate">{goalLabel}: {Mask.formatCurrency(data.goal_value)}</span>
            <span className="ml-auto text-xs font-bold flex-shrink-0">{progress.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2 mb-2">
            <div
              className="h-full rounded-full bg-white transition-all duration-500"
              style={{ width: `${progressClamped}%` }}
            />
          </div>
          <p className="text-xs text-emerald-200 truncate">
            {isGoalReached
              ? <span className="font-semibold text-white">Meta atingida!</span>
              : <>Faltam <span className="font-semibold text-white">{Mask.formatCurrency(data.remaining)}</span></>
            }
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 pt-3 border-t border-white/20">
          <ShoppingBag className="h-4 w-4 text-emerald-200" />
          <span className="text-sm text-emerald-100">
            <span className="font-semibold text-white">{data.order_count}</span> pedidos no mês
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
